import { useInfiniteQuery } from '@tanstack/react-query';
import { WS_INVALIDATED_QUERY_STALE_TIME } from '../constants';
import { operations, SchemaPositionFillDto } from '../generated/apiTypes';
import { fetchClient } from '../lib/apiClient';
import { Gwei } from '../lib/gwei';
import { PrettyMerge } from '../lib/tsUtilityTypes';

type PositionFillsParams = PrettyMerge<
  operations['PositionController_listFills']['parameters']['query'],
  { positionId: string | undefined }
>;

const transformPositionFill = (fill: SchemaPositionFillDto) => ({
  ...fill,
  filled: Gwei.toGwei(fill.filled),
  price: Gwei.toGwei(fill.price),
  feeUsd: Gwei.toGwei(fill.feeUsd),
});

export const usePositionFills = ({
  order = 'asc',
  limit = 100,
  orderBy = 'createdAt',
  positionId,
}: PositionFillsParams) => {
  return useInfiniteQuery({
    queryKey: ['positionPnl', { positionId, limit, order, orderBy }] as const,
    enabled: Boolean(positionId),
    initialPageParam: '',
    staleTime: WS_INVALIDATED_QUERY_STALE_TIME,
    queryFn: async ({ pageParam, signal }) => {
      if (!positionId) {
        throw new Error('Position ID is required');
      }

      const { data } = await fetchClient.GET('/v1/position/fill', {
        signal,
        params: {
          query: {
            positionId,
            limit,
            cursor: pageParam || undefined,
            order,
            orderBy,
          },
        },
      });
      const fills = data?.data.map(transformPositionFill) ?? [];

      return { fills, nextCursor: data?.nextCursor };
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  });
};
